import React from 'react'
import styled from 'styled-components'
import useShop from '../context/AppContext'
import { Helmet } from 'react-helmet'
import { motion } from 'framer-motion'
import { Layout } from '../layout'
import { images } from '../constants'
import { IconSocial } from '../assets/icons'
import { ContactForm, PaperPlane } from '../components'
import { followLinks } from '../config'

const SectionWrapper = styled.div`
  display: flex;
  flex-direction: row;
  overflow: hidden;
  align-items: center;
  width: 100%;
  min-height: calc(100dvh - 100px);
`
const SectionContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 86%;
  max-width: var(--max-width);
  padding: 80px 0 120px;
  margin: 0 auto;
  @media screen and (max-width: 800px) {
    flex-direction: column;
    width: calc(100% - 50px);
    margin: 0 25px;
    padding: 50px 0 100px;
  }
`
const LeftColumn = styled.div`
  width: 50%;
  padding: 25px 0 0 0;
  @media screen and (max-width: 800px) {
    width: 100%;
    padding: 0 25px 50px 25px;
  }
`
const ProfileColumn = styled.div`
  display: flex;
  flex-direction: row;
  margin-bottom: 15px;
  @media screen and (max-width: 800px) {
    justify-content: center;
  }
`
const ProfileLogo = styled.div`
  overflow: hidden;
  width: 48px;
  height: 48px;
  border: 1px solid var(--black);
  border-radius: 50%;
  background-size: 100%;
  background-repeat: no-repeat;
  background-position: 0 0;
  background-image: url(${images.contact_pink_avatar});
  background-color: var(--black-light);
`
const ProfileImage = styled.div`
  position: relative;
  left: -24px;
  z-index: 1;
  overflow: hidden;
  width: 48px;
  height: 48px;
  border: 1px solid var(--black);
  border-radius: 50%;
  background-size: 100%;
  background-repeat: no-repeat;
  background-position: 0 0;
  background-image: url(${images.profile});
  background-color: var(--black-light);
`
const TitleColumn = styled.div`
  display: flex;
  width: 100%;
  padding: 0 40px 0 0;
  h1 {
    line-height: 1.25;
    margin-bottom: 20px;
    text-align: left;
  }
  @media screen and (max-width: 800px) {
    padding: 0;
    justify-content: center;
    h1 {
      text-align: center;
    }
  }
`
const ProfileDesc = styled.p`
  margin: 0 0 60px;
  padding-right: 30px;
  h3,
  p {
    font-size: var(--fz-lg);
    color: var(--text);
    line-height: 1.5;
    font-weight: 400;
  }
  h3 {
    margin-bottom: 15px;
  }

  @media screen and (max-width: 800px) {
    margin: 0;
    padding-right: 0px;
    h3,
    p {
      text-align: center;
    }
  }
`
const ProfileSocial = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  p {
    margin: 0 0 30px;
    color: var(--title);
    font-family: var(--font-sans);
    font-size: var(--fz-lg);
    font-weight: 600;
    line-height: 1.25;
  }
  .social-links {
    display: flex;
    flex-direction: row;
    a {
      display: block;
      width: 44px;
      height: 44px;
      padding: 10px;
      margin: 0 4px;
      border-radius: 50%;
      overflow: hidden;
      background-color: var(--black-medium);
      svg {
        width: 24px;
        height: 24px;
      }
      &:hover {
        color: var(--white);
        background-color: var(--grey-hover);
      }
    }
  }
  @media screen and (max-width: 800px) {
    display: none;
  }
`
const RightColumn = styled.div`
  position: relative;
  width: 50%;
  @media screen and (max-width: 800px) {
    width: 100%;
  }
`
const BackgroundGrid = styled.div`
  opacity: 0.35;
  position: absolute;
  top: -70px;
  left: -120px;
  width: 804px;
  height: 804px;
  background-repeat: no-repeat;
  background-position: 0 0;
  background-size: 804px 804px;
  background-image: url(${images.contact_grid_bg});
  @media screen and (max-width: 800px) {
    top: -180px;
    left: -40px;
  }
`
const ContactSocial = styled.div`
  display: none;
  @media screen and (max-width: 800px) {
    display: block;
    p {
      margin: 80px 0 30px;
      color: var(--title);
      font-family: var(--font-sans);
      font-size: var(--fz-lg);
      font-weight: 600;
      line-height: 1.25;
      text-align: center;
    }
    .social-links {
      display: flex;
      flex-direction: row;
      justify-content: center;
      width: 100%;
      a {
        display: block;
        width: 44px;
        height: 44px;
        padding: 10px;
        margin: 0 4px;
        border-radius: 50%;
        overflow: hidden;
        background-color: var(--black-medium);
        svg {
          width: 24px;
          height: 24px;
        }
        &:hover {
          color: var(--white);
          background-color: var(--grey-hover);
        }
      }
    }
  }
`

const Contact = () => {
  const { preloaderExecuted, navigationExecuted } = useShop()
  const title = <h1>Let me help you bring your project to life!</h1>
  const subtitle = (
    <h3>
      Feel free to reach out to me via my contact form or email me at{' '}
      <a href="mailto:robert@roberteberhard.com?subject=Let me help you bring your project to life!" class="secondary-link">
        robert@roberteberhard.com
      </a>
      .
    </h3>
  )
  const heading = <p>I look forward to hearing from you!</p>
  const socials = <p>Social</p>

  return (
    <Layout>
      <Helmet>
        <title>Contact Me – Robert Eberhard</title>
      </Helmet>
      <SectionWrapper>
        <SectionContainer>
          {preloaderExecuted && navigationExecuted ? (
            <LeftColumn>
              <ProfileColumn>
                <motion.div animate={{ x: [-15, 0], opacity: [0, 1] }} transition={{ delay: 0.2, duration: 0.3 }} className="start-opacity">
                  <ProfileLogo />
                </motion.div>
                <motion.div animate={{ x: [-15, 0], opacity: [0, 1] }} transition={{ delay: 0.1, duration: 0.3 }} className="start-opacity">
                  <ProfileImage />
                </motion.div>
              </ProfileColumn>
              <TitleColumn>
                <motion.div animate={{ y: [15, 0], opacity: [0, 1] }} transition={{ delay: 0.3, duration: 0.3 }} className="start-opacity">
                  {title}
                </motion.div>
              </TitleColumn>
              <ProfileDesc>
                <motion.div animate={{ y: [15, 0], opacity: [0, 1] }} transition={{ delay: 0.4, duration: 0.3 }} className="start-opacity">
                  {subtitle}
                  {heading}
                </motion.div>
              </ProfileDesc>
              <ProfileSocial>
                <motion.div animate={{ y: [15, 0], opacity: [0, 1] }} transition={{ delay: 0.5, duration: 0.3 }} className="start-opacity">
                  {socials}
                </motion.div>
                <div className="social-links">
                  {followLinks &&
                    followLinks.map(({ name, url }, i) => (
                      <motion.div key={i} animate={{ y: [15, 0], opacity: [0, 1] }} transition={{ delay: i * 0.1 + 0.6, duration: 0.3 }} className="start-opacity">
                        <a href={url} target="_blank" aria-label={name}>
                          <IconSocial name={name} />
                        </a>
                      </motion.div>
                    ))}
                </div>
              </ProfileSocial>
              <motion.div animate={{ opacity: [0, 1] }} transition={{ delay: 1.1, duration: 1 }} className="start-opacity">
                <PaperPlane />
              </motion.div>
            </LeftColumn>
          ) : (
            <LeftColumn>
              <ProfileColumn>
                <motion.div animate={{ x: [-15, 0], opacity: [0, 1] }} transition={{ delay: 1.1, duration: 0.3 }} className="start-opacity">
                  <ProfileLogo />
                </motion.div>
                <motion.div animate={{ x: [-15, 0], opacity: [0, 1] }} transition={{ delay: 1, duration: 0.3 }} className="start-opacity">
                  <ProfileImage />
                </motion.div>
              </ProfileColumn>
              <TitleColumn>
                <motion.div animate={{ y: [15, 0], opacity: [0, 1] }} transition={{ delay: 1.2, duration: 0.3 }} className="start-opacity">
                  {title}
                </motion.div>
              </TitleColumn>
              <ProfileDesc>
                <motion.div animate={{ y: [15, 0], opacity: [0, 1] }} transition={{ delay: 1.3, duration: 0.3 }} className="start-opacity">
                  {subtitle}
                  {heading}
                </motion.div>
              </ProfileDesc>
              <ProfileSocial>
                <motion.div animate={{ y: [15, 0], opacity: [0, 1] }} transition={{ delay: 1.4, duration: 0.3 }} className="start-opacity">
                  {socials}
                </motion.div>
                <div className="social-links">
                  {followLinks &&
                    followLinks.map(({ name, url }, i) => (
                      <motion.div key={i} animate={{ y: [15, 0], opacity: [0, 1] }} transition={{ delay: i * 0.1 + 1.5, duration: 0.3 }} className="start-opacity">
                        <a href={url} target="_blank" aria-label={name}>
                          <IconSocial name={name} />
                        </a>
                      </motion.div>
                    ))}
                </div>
              </ProfileSocial>
              <motion.div animate={{ opacity: [0, 1] }} transition={{ delay: 2, duration: 1 }} className="start-opacity">
                <PaperPlane />
              </motion.div>
            </LeftColumn>
          )}
          {preloaderExecuted && navigationExecuted ? (
            <RightColumn>
              <BackgroundGrid />
              <motion.div animate={{ scale: [1.05, 1], opacity: [0, 1] }} transition={{ delay: 0.5, duration: 0.5 }} className="start-opacity">
                <ContactForm />
              </motion.div>
              <motion.div animate={{ y: [15, 0], opacity: [0, 1] }} transition={{ delay: 0.7, duration: 0.3 }} className="start-opacity">
                <ContactSocial>
                  {socials}
                  <div className="social-links">
                    {followLinks &&
                      followLinks.map(({ name, url }, i) => (
                        <a href={url} key={i} target="_blank" aria-label={name}>
                          <IconSocial name={name} />
                        </a>
                      ))}
                  </div>
                </ContactSocial>
              </motion.div>
            </RightColumn>
          ) : (
            <RightColumn>
              <BackgroundGrid />
              <motion.div animate={{ scale: [1.05, 1], opacity: [0, 1] }} transition={{ delay: 1.3, duration: 0.5 }} className="start-opacity">
                <ContactForm />
              </motion.div>
              <motion.div animate={{ y: [15, 0], opacity: [0, 1] }} transition={{ delay: 1.5, duration: 0.3 }} className="start-opacity">
                <ContactSocial>
                  {socials}
                  <div className="social-links">
                    {followLinks &&
                      followLinks.map(({ name, url }, i) => (
                        <a href={url} key={i} target="_blank" aria-label={name}>
                          <IconSocial name={name} />
                        </a>
                      ))}
                  </div>
                </ContactSocial>
              </motion.div>
            </RightColumn>
          )}
        </SectionContainer>
      </SectionWrapper>
    </Layout>
  )
}

export default Contact
